import { getToken, onMessage } from "firebase/messaging";

import {
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import {
  auth,
  db,
  messaging,
} from "./firebase";


/* ============================================================
   VAPID KEY
============================================================ */

const VAPID_KEY =
  "BASsDtLl6vBLQQIoeVfg1SZRwb7aGmSw3-UFvkZsrPc3Hdx4I9MuvKmHZXqM-uQe3plIx565lTF7ENZeFauMvC8";


/* ============================================================
   FOREGROUND MESSAGE LISTENER
   ============================================================

   Keep ONE listener only.

   If listenNotifications() gets called again, the previous
   listener is unsubscribed before creating a new one.
============================================================ */

let unsubscribeForeground = null;


/* ============================================================
   ENABLE PUSH NOTIFICATIONS
============================================================ */

export async function enableNotifications() {
  try {

    /* --------------------------------------------------------
       1. Check Firebase Messaging
    -------------------------------------------------------- */

    if (!messaging) {
      console.log(
        "❌ Firebase Messaging is not available"
      );

      return null;
    }


    /* --------------------------------------------------------
       2. Check browser notification support
    -------------------------------------------------------- */

    if (
      typeof Notification === "undefined"
    ) {
      console.log(
        "❌ This browser does not support notifications"
      );

      return null;
    }


    /* --------------------------------------------------------
       3. Ask notification permission
    -------------------------------------------------------- */

    let permission =
      Notification.permission;

    /*
     * Only request permission if it has not already
     * been decided.
     */

    if (permission === "default") {
      permission =
        await Notification.requestPermission();
    }

    console.log(
      "🔔 Notification permission:",
      permission
    );


    if (permission !== "granted") {

      console.log(
        "❌ Notification permission denied"
      );

      return null;
    }


    /* --------------------------------------------------------
       4. Make sure service worker exists
    -------------------------------------------------------- */

    if (
      !("serviceWorker" in navigator)
    ) {

      console.log(
        "❌ Service Worker is not supported"
      );

      return null;
    }


    /* --------------------------------------------------------
       5. Wait for service worker
    -------------------------------------------------------- */

    const registration =
      await navigator.serviceWorker.ready;

    console.log(
      "✅ Service Worker ready:",
      registration.scope
    );


    /* --------------------------------------------------------
       6. Get FCM token
    -------------------------------------------------------- */

    const token =
      await getToken(
        messaging,
        {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration:
            registration,
        }
      );


    if (!token) {

      console.log(
        "❌ No FCM token received"
      );

      return null;
    }


    console.log(
      "🔥 FCM Token:",
      token
    );


    /* --------------------------------------------------------
       7. Get current logged-in user
    -------------------------------------------------------- */

    const user =
      auth.currentUser;


    if (!user) {

      console.log(
        "⚠️ No logged-in user."
      );

      console.log(
        "Token generated but not saved."
      );

      return token;
    }


    /* --------------------------------------------------------
       8. Create safe token document ID
    -------------------------------------------------------- */

    const tokenId =
      btoa(token)
        .replace(
          /[^a-zA-Z0-9]/g,
          ""
        )
        .slice(0, 100);


    /* --------------------------------------------------------
       9. Save token
    -------------------------------------------------------- */

    const tokenRef =
      doc(
        db,
        "users",
        user.uid,
        "fcmTokens",
        tokenId
      );


    await setDoc(
      tokenRef,
      {
        token: token,

        origin:
          window.location.origin,

        userAgent:
          navigator.userAgent,

        updatedAt:
          serverTimestamp(),
      },
      {
        merge: true,
      }
    );


    console.log(
      "✅ FCM token saved successfully"
    );

    console.log(
      "🌐 Origin:",
      window.location.origin
    );

    console.log(
      "👤 User:",
      user.uid
    );


    return token;

  } catch (error) {

    console.error(
      "❌ enableNotifications() failed:",
      error
    );

    return null;
  }
}


/* ============================================================
   FOREGROUND NOTIFICATIONS
============================================================ */

export function listenNotifications() {

  /* ----------------------------------------------------------
     Check Firebase Messaging
  ---------------------------------------------------------- */

  if (!messaging) {

    console.log(
      "❌ Firebase Messaging is not available"
    );

    return () => {};
  }


  /* ----------------------------------------------------------
     IMPORTANT:
     Remove previous foreground listener
  ---------------------------------------------------------- */

  if (
    typeof unsubscribeForeground ===
    "function"
  ) {

    console.log(
      "🧹 Removing previous foreground listener"
    );

    unsubscribeForeground();

    unsubscribeForeground = null;
  }


  /* ----------------------------------------------------------
     Create ONE foreground listener
  ---------------------------------------------------------- */

  unsubscribeForeground =
    onMessage(
      messaging,
      (payload) => {

        console.log(
          "🔥 FOREGROUND MESSAGE"
        );

        console.log(
          payload
        );


        /* ----------------------------------------------------
           Notification data
        ---------------------------------------------------- */

        const notification =
          payload?.notification || {};


        const title =
          notification.title ||
          "Eyal App";


        const body =
          notification.body ||
          "";


        const icon =
          notification.icon ||
          "/images/icons/192x192.png";


        /* ----------------------------------------------------
           Foreground browser notification
        ---------------------------------------------------- */

        if (
          typeof Notification !==
          "undefined" &&
          Notification.permission ===
          "granted"
        ) {

          try {

            const browserNotification =
              new Notification(
                title,
                {
                  body,
                  icon,

                  /*
                   * Keep the notification data
                   * available for future click handling.
                   */

                  data:
                    payload?.data || {},
                }
              );


            /* ----------------------------------------------
               Optional click handling
            ---------------------------------------------- */

            browserNotification.onclick =
              () => {

                window.focus();

                const clickUrl =
                  payload?.data?.clickUrl ||
                  "/";

                window.location.href =
                  clickUrl;
              };


          } catch (error) {

            console.error(
              "❌ Foreground notification failed:",
              error
            );

          }

        } else {

          console.log(
            "⚠️ Notification permission is not granted"
          );

        }
      }
    );


  console.log(
    "✅ Foreground notification listener registered"
  );


  /* ----------------------------------------------------------
     Return unsubscribe function
  ---------------------------------------------------------- */

  return unsubscribeForeground;
}


/* ============================================================
   CLEANUP FOREGROUND LISTENER
============================================================ */

export function stopListeningNotifications() {

  if (
    typeof unsubscribeForeground ===
    "function"
  ) {

    console.log(
      "🧹 Stopping foreground notification listener"
    );

    unsubscribeForeground();

    unsubscribeForeground = null;
  }
}